import { getHomeOwnerWorldApi } from '@/helpers/Api';
import { createHeaders, getAxiosClient } from '@/helpers/Axios/axiosClient';
import { MyPropertiesResult } from '@/models/queries/getMyProperties';
import { GetPropertyDataResponse, PropertyValuationResult } from '@/models/queries/getPropertyData';
import { mapHomeOwnerWorldPropertyToPropertyValuationResult } from '@/store/modules/propertyValuation/services';
import {
  HomeOwnerWorldPropertiesResponse,
  HomeOwnerWorldProperty,
  HomeOwnerWorldPropertyFromValuationFunnelRequest,
  HomeOwnerWorldPropertyLinkResponse,
  HomeOwnerWorldPropertyResponse,
  HomeOwnerWorldPropertyUpdateRequest,
} from '@/store/modules/propertyValuation/types';
import { AXIOS_HEADERS } from '@/types';
import { captureError } from '@smg-real-estate/browser-rum';
import retry, { Options } from 'async-retry';
import { AxiosResponse, HttpStatusCode } from 'axios';

export const getMyProperties = async (): Promise<MyPropertiesResult | null> => {
  try {
    const response: AxiosResponse<HomeOwnerWorldProperty[] | HomeOwnerWorldPropertiesResponse> =
      await getAxiosClient().get(getHomeOwnerWorldApi('properties'));

    const { data } = response;

    const properties = (data as HomeOwnerWorldPropertiesResponse).data || data;
    const meta = (data as HomeOwnerWorldPropertiesResponse).meta;

    return {
      data: properties.map((item) => mapHomeOwnerWorldPropertyToPropertyValuationResult(item)),
      canUserCreate: meta?.canUserCreate ?? true,
    };
  } catch (e) {
    captureError(e);
    return null;
  }
};

export const deletePropertyById = async (propertyId: string): Promise<boolean> => {
  try {
    const response: AxiosResponse = await getAxiosClient().delete(
      getHomeOwnerWorldApi(`properties/${propertyId}`),
    );
    return response.status === HttpStatusCode.Ok;
  } catch (e) {
    captureError(e);
    return false;
  }
};

export const updatePropertyById = async (
  propertyId: string,
  payload: HomeOwnerWorldPropertyUpdateRequest,
): Promise<PropertyValuationResult | null> => {
  try {
    const response: AxiosResponse<HomeOwnerWorldProperty> = await getAxiosClient().patch(
      getHomeOwnerWorldApi(`properties/${propertyId}`),
      payload,
    );

    return response?.data
      ? mapHomeOwnerWorldPropertyToPropertyValuationResult(response.data)
      : null;
  } catch (e) {
    captureError(e);
    return null;
  }
};

export enum API {
  PropertyValuation = 'property-valuation',
  HomeOwnerWorld = 'home-owner-world',
}

/**
 * For Result Page & Property Details Page, with retries 2 times
 * @param id - Either the lead ID of property valuation or the property ID of home owner world
 * @param retries - Number of retries for the request
 * @param api - API to fetch data from (PropertyValuation or HomeOwnerWorld)
 * @param isPublic - Whether the request is from a public source
 * @returns Property valuation data or null if failed
 */
export async function fetchPropertyData(
  id: string,
  retries: number,
  api: API,
  isPublic = false,
): Promise<
  GetPropertyDataResponse<GetPropertyDataResponse<PropertyValuationResult | HomeOwnerWorldProperty>>
> {
  const apiEndpoint = getHomeOwnerWorldApi(`properties/${id}`);
  const customHeaders = isPublic
    ? createHeaders({ [AXIOS_HEADERS.REQUEST_SOURCE]: 'public' })
    : undefined;
  return await retry(
    async (bail, attempt) => {
      try {
        const response: AxiosResponse<
          GetPropertyDataResponse<
            GetPropertyDataResponse<PropertyValuationResult | HomeOwnerWorldProperty>
          >
        > = await getAxiosClient(customHeaders).get(apiEndpoint);
        const { data } = response || {};

        if (!data?.appraisalPrice) {
          // eslint-disable-next-line max-depth
          if (attempt > retries) {
            const error = new Error(
              `Retries exhausted, result data is invalid. id: ${id} for the api: ${api}`,
            );
            captureError(error);
            throw error;
          }
          throw new Error('Retrying ...'); // Retry the request
        }

        return api === API.HomeOwnerWorld
          ? mapHomeOwnerWorldPropertyToPropertyValuationResult(data as HomeOwnerWorldProperty)
          : data;
      } catch (error) {
        if (error?.response?.status === HttpStatusCode.NotFound) {
          bail(error);
          return null;
        }

        if (attempt > retries) {
          captureError(error);
          bail(error);
          return null;
        }

        throw error; // Retry the request
      }
    },
    { retries, minTimeout: 1000, maxTimeout: 1000 } as Options,
  );
}

export const getPropertyIdByObjectReference = async (
  objectReference: string,
): Promise<string | null> => {
  try {
    const response: AxiosResponse<{ propertyId: string }> = await getAxiosClient().get(
      getHomeOwnerWorldApi(`properties/by-object-reference/${encodeURIComponent(objectReference)}`),
    );
    return response.data.propertyId;
  } catch (e) {
    captureError(e);
    return null;
  }
};

export interface CreatePropertyFromInsertionOnDemandResult {
  propertyId: string;
  propertyData: HomeOwnerWorldPropertyResponse | null;
}

const getExistingInsertionPropertyId = (error: any): string | null => {
  if (error?.response?.status !== HttpStatusCode.Conflict) {
    return null;
  }

  return error.response.data?.data?.propertyId ?? error.response.data?.propertyId ?? null;
};

export const createPropertyFromInsertionOnDemand = async (
  objectRef: string,
): Promise<CreatePropertyFromInsertionOnDemandResult | null> => {
  try {
    const response: AxiosResponse<HomeOwnerWorldPropertyResponse> = await getAxiosClient().post(
      getHomeOwnerWorldApi(`properties/from-insertion/${encodeURIComponent(objectRef)}`),
    );
    return { propertyId: response.data.propertyId, propertyData: response.data };
  } catch (error: any) {
    const propertyId = getExistingInsertionPropertyId(error);
    if (propertyId) return { propertyId, propertyData: null };

    captureError(error);
    return null;
  }
};

export const connectPropertyToAccountAfterLogin = async (
  propertyId: string,
): Promise<AxiosResponse<HomeOwnerWorldPropertyLinkResponse>> => {
  try {
    return await getAxiosClient().put(
      getHomeOwnerWorldApi(`property-valuation/lead/${propertyId}/link`),
      {},
    );
  } catch (error) {
    captureError(error);
    return {
      status: HttpStatusCode.InternalServerError,
    } as AxiosResponse<HomeOwnerWorldPropertyLinkResponse>;
  }
};

export const createPropertyFromValuationFunnel = async (
  payload: HomeOwnerWorldPropertyFromValuationFunnelRequest,
): Promise<{ isCreated: boolean; propertyId: string }> => {
  try {
    const response: AxiosResponse<HomeOwnerWorldPropertyResponse> = await getAxiosClient().post(
      getHomeOwnerWorldApi('properties/from-valuation-funnel'),
      payload,
    );

    return {
      isCreated: response.status === HttpStatusCode.Created,
      propertyId: response.data.propertyId,
    };
  } catch (e) {
    captureError(e);
    return { isCreated: false, propertyId: '' };
  }
};
